import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { TFn } from "../i18nTypes";
import type { WeekProgress } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { hiddenFromScreenReader } from "./hiddenFromScreenReader";

// <WeekProgressLine> (§8bis.1, sua parola del 15/09): «una settimana parziale
// si dichiara parziale». One line, «Tuesday · 5 days to go», on every surface
// that shows the current calendar week: the Progress coverage card and the
// Home targets card. Built at its second callsite, not before.
//
// `strip` adds the seven-day row the mockup drew on the coverage card (days
// gone filled, today marked, the rest outlined): decorative, hidden from
// screen readers, and never the only carrier (the words say it all). The
// labels are the SHORT weekday names: the narrow ones collide (T/T, S/S in
// English), and at 320pt the short ones still fit, wrapping above the words.

// 2024-01-07 was a Sunday: weekday `w` is this instant + w days, formatted in
// UTC so no zone can move it onto a neighbour.
const SUNDAY_UTC = Date.UTC(2024, 0, 7, 12);
const DAY = 86_400_000;

const weekdayName = (
  weekday: number,
  locale: string,
  style: "long" | "short",
): string =>
  new Intl.DateTimeFormat(locale, { weekday: style, timeZone: "UTC" }).format(
    new Date(SUNDAY_UTC + weekday * DAY),
  );

// Italian and French write weekdays lowercase; at the head of a line it takes
// a capital, in the reader's own casing rules.
const capitalised = (s: string, locale: string): string =>
  s.charAt(0).toLocaleUpperCase(locale) + s.slice(1);

/** The visible line and the spoken one: the middle dot is punctuation for the
 *  eye, and a screen reader gets commas and the name of the thing instead. */
export const weekProgressText = (
  t: TFn,
  locale: string,
  week: WeekProgress,
): { text: string; a11y: string } => {
  const day = capitalised(weekdayName(week.weekday, locale, "long"), locale);
  const rest =
    week.daysLeft === 0
      ? t("common.weekLastDay", { defaultValue: "last day" })
      : t("common.weekDaysToGo", { count: week.daysLeft });
  return {
    text: `${day} · ${rest}`,
    a11y: `${t("common.weekInProgress", { defaultValue: "Week in progress" })}: ${day}, ${rest}`,
  };
};

export type WeekProgressLineProps = {
  theme: UiTheme;
  week: WeekProgress;
  locale: string;
  strip?: boolean;
};

export function WeekProgressLine({
  theme,
  week,
  locale,
  strip = false,
}: WeekProgressLineProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const { text, a11y } = weekProgressText(t, locale, week);
  const startDay = (week.weekday - week.dayIndex + 7) % 7;
  return (
    <View style={styles.row} accessible accessibilityLabel={a11y}>
      {strip ? (
        <View style={styles.days} {...hiddenFromScreenReader()}>
          {Array.from({ length: 7 }, (_, i) => (
            <View key={i} style={styles.day}>
              <View
                style={[
                  styles.box,
                  i < week.dayIndex ? styles.boxDone : null,
                  i === week.dayIndex ? styles.boxToday : null,
                ]}
              />
              <Text
                style={[
                  styles.dayLbl,
                  i === week.dayIndex ? styles.dayLblToday : null,
                ]}
              >
                {weekdayName((startDay + i) % 7, locale, "short")}
              </Text>
              {/* The shape cue: a tick under today, so "which day" never
                  rests on a fill two inks apart. */}
              <View
                style={[styles.tick, i === week.dayIndex ? styles.tickToday : null]}
              />
            </View>
          ))}
        </View>
      ) : null}
      <Text style={styles.text}>{text}</Text>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    row: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      columnGap: s.s3,
      rowGap: s.s1,
    },
    days: { flexDirection: "row", gap: s.s1 },
    day: { alignItems: "center", gap: 2, minWidth: 18 },
    // Gone = `ink3` fill, to come = `ink3` outline, TODAY = a solid `ink`
    // fill, taller, with a tick under its label. ⛔ Not `ink2` vs `ink3`
    // (1.44:1): today is told by weight and shape, never by a near hue.
    box: {
      width: 14,
      height: 8,
      borderWidth: 1,
      borderColor: p.ink3,
      borderRadius: r.r1,
    },
    boxDone: { backgroundColor: p.ink3 },
    boxToday: { backgroundColor: p.ink, borderColor: p.ink, height: 12 },
    tick: { width: 12, height: 3, borderRadius: r.r1 },
    tickToday: { backgroundColor: p.ink },
    // sm, the DS-16 floor, even though the strip is decorative: it is still
    // text on screen that a reduced-vision reader will try to read.
    dayLbl: { color: p.ink3, fontSize: ty.sizes.sm },
    dayLblToday: { color: p.ink, fontWeight: "700" },
    // `ink`, not a muted ink: this line is what makes every number below it
    // honest, and it has to be read before them.
    text: { color: p.ink, fontSize: ty.sizes.sm, fontWeight: "600" },
  });
}
